import { useEffect, useState, useCallback } from 'react';
import { Calendar, FileText, AlertCircle, ArrowLeft, Search, Loader2, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getDigests, getDigest, mcGetChains, mcGetTokenOverview, mcGetTokenOhlcv } from '../lib/api';
import { fmtUsd, fmtPrice, shortAddr } from '../lib/numfmt';
import DOMPurify from 'dompurify';

/**
 * /archive — historical daily activity.
 *
 * • Krown: browse the bot's persisted daily ecosystem digests (unchanged).
 * • Any other chain: a per-token Daily Recap built from GeckoTerminal daily
 *   candles (date → close, % change, volume, high, low) — an honest "archive"
 *   that works on every chain without a bespoke indexer.
 */
export default function ArchivePage() {
  const [chains, setChains] = useState([]);
  const [chain, setChain] = useState('krown');

  useEffect(() => {
    mcGetChains().then((d) => setChains(d.chains || [])).catch(() => {});
  }, []);

  const chainLabel = chains.find((c) => c.id === chain)?.label || chain;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="archive-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-amber-500" /> Archive
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {chain === 'krown'
              ? 'All past daily digests rendered by the Telegram bot are persisted here.'
              : `Day-by-day market recap for any token on ${chainLabel}, from pool candles.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={chain} onValueChange={setChain} data-testid="archive-chain-select-root">
            <SelectTrigger className="w-[190px]" data-testid="archive-chain-select">
              <SelectValue placeholder="Select chain" />
            </SelectTrigger>
            <SelectContent>
              {chains.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`archive-chain-opt-${c.id}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Link to="/" data-testid="archive-home-link">
            <Button variant="outline" size="sm" data-testid="archive-back-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
          </Link>
        </div>
      </div>

      {chain === 'krown'
        ? <KrownDigestArchive />
        : <DailyRecap chain={chain} chainLabel={chainLabel} />}
    </div>
  );
}

// -------------------------------------------------------------------------
// Krown — persisted bot digests
// -------------------------------------------------------------------------
function KrownDigestArchive() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoadingList(true);
    getDigests({ limit: 90 })
      .then((data) => {
        if (!mounted) return;
        setList(data.digests || []);
        if (data.digests?.length) setSelected(data.digests[0].date);
      })
      .catch((e) => mounted && setError(e?.message || 'Failed to load digests'))
      .finally(() => mounted && setLoadingList(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selected) return;
    let mounted = true;
    setLoadingDetail(true);
    setDetail(null);
    getDigest(selected)
      .then((d) => mounted && setDetail(d))
      .catch((e) => mounted && setError(e?.message || 'Failed to load digest'))
      .finally(() => mounted && setLoadingDetail(false));
    return () => { mounted = false; };
  }, [selected]);

  return (
    <>
      {error && (
        <Card className="border-red-900/40 bg-red-950/30">
          <CardContent className="p-4 flex items-center gap-2 text-red-300">
            <AlertCircle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <Card className="h-fit" data-testid="archive-date-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" /> {loadingList ? 'Loading…' : `${list.length} digest(s)`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 max-h-[70vh] overflow-y-auto">
            {loadingList ? (
              <div className="space-y-2 p-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : list.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3">
                No digests yet. They appear here after the bot sends one (00:00 UTC daily),
                or run <code className="text-amber-400">python scripts/backfill_digests.py --days 30</code>.
              </p>
            ) : (
              list.map((d) => (
                <button
                  key={d.date}
                  onClick={() => setSelected(d.date)}
                  data-testid={`archive-date-${d.date}`}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                    selected === d.date
                      ? 'bg-amber-600/20 text-amber-400 font-medium'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{d.date}</span>
                    {d.backfilled && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">backfill</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="min-h-[60vh]" data-testid="archive-detail">
          <CardHeader className="pb-3 border-b border-border">
            <CardTitle className="text-base flex items-center gap-2">
              {detail ? (
                <>
                  <span className="font-mono text-amber-400">{detail.date}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground font-normal">{detail.label}</span>
                </>
              ) : (
                <span className="text-muted-foreground">Select a date</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {loadingDetail ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : detail ? (
              <article
                className="prose prose-invert prose-sm max-w-none font-mono whitespace-pre-wrap leading-relaxed"
                data-testid="archive-digest-body"
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detail.text || '') }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No digest selected.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// -------------------------------------------------------------------------
// Any other chain — per-token daily recap from GeckoTerminal candles
// -------------------------------------------------------------------------
function buildRows(candles) {
  return (candles || [])
    .map((c) => {
      const o = c.o;
      const change = o ? ((c.c - o) / o) * 100 : null;
      return {
        date: new Date(c.ts * 1000).toISOString().slice(0, 10),
        close: c.c, high: c.h, low: c.l, volume: c.v, change,
      };
    })
    .reverse();
}

function DailyRecap({ chain, chainLabel }) {
  const [input, setInput] = useState('');
  const [token, setToken] = useState(null);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset when the chain changes.
  useEffect(() => { setToken(null); setRows(null); setError(null); setInput(''); }, [chain]);

  const run = useCallback(async (addr) => {
    const a = (addr || '').trim();
    if (!a) return;
    setLoading(true); setError(null); setToken(null); setRows(null);
    try {
      const ov = await mcGetTokenOverview(chain, a);
      if (!ov || !ov.indexed) {
        setError('No market data found for this token on this chain.');
        return;
      }
      setToken(ov);
      const oh = await mcGetTokenOhlcv(chain, a, '1d');
      setRows(buildRows(oh?.candles));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load this token. Check the contract address.');
    } finally {
      setLoading(false);
    }
  }, [chain]);

  return (
    <div className="space-y-5" data-testid="archive-recap">
      <form
        onSubmit={(e) => { e.preventDefault(); run(input); }}
        className="flex flex-col sm:flex-row gap-2"
        data-testid="recap-search-form"
      >
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="recap-search-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Token contract on ${chainLabel}`}
            className="pl-10 font-mono"
          />
        </div>
        <Button type="submit" data-testid="recap-search-btn" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Build recap</>}
        </Button>
      </form>

      {error && (
        <Card className="border-red-900/40 bg-red-950/30" data-testid="recap-error">
          <CardContent className="p-4 flex items-center gap-2 text-red-300">
            <AlertCircle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && <Skeleton className="h-64 w-full" />}

      {!loading && !error && !token && (
        <Card className="border-dashed" data-testid="recap-empty">
          <CardContent className="pt-8 pb-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <LineChart className="w-6 h-6 opacity-60" />
            Paste a token contract above to see its daily price &amp; volume history on {chainLabel}.
          </CardContent>
        </Card>
      )}

      {token && rows && (
        <>
          <Card data-testid="recap-token-header">
            <CardContent className="pt-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="min-w-0">
                  <div className="font-bold text-lg truncate">
                    {token.symbol || shortAddr(token.address)}
                    <span className="text-muted-foreground font-normal text-sm ml-2">{token.name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{shortAddr(token.address)}</div>
                </div>
              </div>
              <div className="flex items-center gap-5 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Price</div>
                  <div className="font-mono font-semibold">{fmtPrice(token.price_usd)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Liquidity</div>
                  <div className="font-mono font-semibold">{fmtUsd(token.liquidity_usd)}</div>
                </div>
                <Link
                  to={`/explorer/${chain}/token/${token.address}`}
                  className="text-primary text-xs hover:underline font-medium"
                  data-testid="recap-open-token"
                >
                  Open token →
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="recap-table-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">
                Daily recap · {rows.length} day(s)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {rows.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">No daily candles available for this token.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]" data-testid="recap-table">
                    <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Date</th>
                        <th className="text-right px-4 py-2 font-medium">Close</th>
                        <th className="text-right px-4 py-2 font-medium">Change</th>
                        <th className="text-right px-4 py-2 font-medium">Volume</th>
                        <th className="text-right px-4 py-2 font-medium">High</th>
                        <th className="text-right px-4 py-2 font-medium">Low</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rows || []).map((r) => {
                        const up = r.change != null && r.change >= 0;
                        return (
                          <tr key={r.date} className="border-b border-border/50 hover:bg-muted/30" data-testid={`recap-row-${r.date}`}>
                            <td className="px-4 py-2 font-mono text-xs">{r.date}</td>
                            <td className="px-4 py-2 text-right font-mono">{fmtPrice(r.close)}</td>
                            <td className={`px-4 py-2 text-right font-mono ${r.change == null ? 'text-muted-foreground' : up ? 'text-emerald-500' : 'text-red-500'}`}>
                              {r.change == null ? '—' : `${up ? '+' : ''}${r.change.toFixed(2)}%`}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">{fmtUsd(r.volume)}</td>
                            <td className="px-4 py-2 text-right font-mono text-emerald-500/80">{fmtPrice(r.high)}</td>
                            <td className="px-4 py-2 text-right font-mono text-red-500/80">{fmtPrice(r.low)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
